import { apiClient } from './axios';

import type { ReplayResult, ReplayStatus } from './replays.api';

export type ScenarioTransport = 'PSTN' | 'WEBRTC' | 'WHATSAPP' | 'WEB_WIDGET' | 'EMAIL';
export type ScenarioModality = 'VOICE' | 'TEXT';

export interface ScenarioChannel {
  transport: ScenarioTransport;
  modality: ScenarioModality;
}

export interface ScenarioChannelOption extends ScenarioChannel {
  label: string;
}

/** The seven valid transport/modality pairs. */
export const SCENARIO_CHANNELS: readonly ScenarioChannelOption[] = [
  { transport: 'PSTN', modality: 'VOICE', label: 'Phone (voice)' },
  { transport: 'WEBRTC', modality: 'VOICE', label: 'WebRTC (voice)' },
  { transport: 'WHATSAPP', modality: 'TEXT', label: 'WhatsApp (text)' },
  { transport: 'WHATSAPP', modality: 'VOICE', label: 'WhatsApp (voice)' },
  { transport: 'WEB_WIDGET', modality: 'TEXT', label: 'Web chat (text)' },
  { transport: 'WEB_WIDGET', modality: 'VOICE', label: 'Widget (voice)' },
  { transport: 'EMAIL', modality: 'TEXT', label: 'Email (text)' },
];

export const scenarioChannelKey = (channel: ScenarioChannel) =>
  `${channel.transport}/${channel.modality}`;

export const scenarioChannelLabel = (channel: ScenarioChannel) =>
  SCENARIO_CHANNELS.find(
    (c) => c.transport === channel.transport && c.modality === channel.modality,
  )?.label ?? scenarioChannelKey(channel);

export interface ScenarioBrief {
  persona: string;
  goal: string;
  facts: string[];
  objections: string[];
  openingLine: string;
}

export interface ScenarioCheck {
  id: string;
  text: string;
}

export interface ScenarioRules {
  toolsMustCall: string[];
  toolsMustNotCall: string[];
  mustSay: string[];
  mustNotSay: string[];
  outcome: string | null;
  maxTurns: number | null;
}

export interface ScenarioExpectations {
  checks: ScenarioCheck[];
  rules: ScenarioRules;
}

export interface Scenario {
  id: string;
  name: string;
  description: string;
  tags: string[];
  brief: ScenarioBrief;
  expectations: ScenarioExpectations;
  turn_cap: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Optional aggregates the list endpoint may include. */
  assignments_count?: number;
  last_result?: ReplayResult | null;
}

export interface ScenarioAssignment {
  id: string;
  scenario_id: string;
  configuration_id: string;
  transport: ScenarioTransport;
  modality: ScenarioModality;
  required: boolean;
  enabled: boolean;
  created_at: string;
  scenario: { id: string; name: string };
  configuration: { id: string; name: string };
}

export type ScenarioRunTargetKind = 'live' | 'branch' | 'commit';
export type ScenarioRunStatus = 'QUEUED' | 'RUNNING' | 'JUDGED' | 'FAILED' | 'CANCELLED';

export interface ScenarioJudgeCheckResult {
  id: string;
  text: string;
  result: ReplayResult;
  reason: string;
  evidence: string | null;
}

export interface ScenarioJudgeRuleResult {
  rule: string;
  result: 'pass' | 'fail';
  detail: string;
}

export interface ScenarioJudgeOutput {
  scenarioName: string;
  checks: ScenarioJudgeCheckResult[];
  rules: ScenarioJudgeRuleResult[];
  endedBy: string;
  verdict: { result: ReplayResult; reasons: string[] };
}

export interface ScenarioRunCase {
  id: string;
  scenario_id: string | null;
  scenario_name: string;
  transport: ScenarioTransport;
  modality: ScenarioModality;
  status: ReplayStatus;
  result: ReplayResult | null;
  test_call_id: string | null;
  judge_output: ScenarioJudgeOutput | null;
}

export interface ScenarioRun {
  id: string;
  configuration_id: string;
  target_kind: ScenarioRunTargetKind;
  branch_id: string | null;
  commit_id: string | null;
  pipeline_run_id: string | null;
  status: ScenarioRunStatus;
  total: number;
  passed: number;
  failed: number;
  inconclusive: number;
  created_by: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
  /** Present on `GET /scenario-runs/:id` (and possibly on filtered lists). */
  cases?: ScenarioRunCase[];
}

export interface CreateScenarioInput {
  name: string;
  description: string;
  tags: string[];
  brief: ScenarioBrief;
  expectations: ScenarioExpectations;
  turnCap: number;
}

export type UpdateScenarioInput = Partial<CreateScenarioInput>;

/** What `POST /scenarios/draft` returns: a scenario body without a saved id. */
export type ScenarioDraft = Partial<CreateScenarioInput>;

export interface CreateAssignmentInput {
  scenarioId: string;
  configurationId: string;
  transport: ScenarioTransport;
  modality: ScenarioModality;
  required?: boolean;
}

export interface UpdateAssignmentInput {
  required?: boolean;
  enabled?: boolean;
}

export interface CreateScenarioRunInput {
  configurationId: string;
  target: { kind: ScenarioRunTargetKind; branchId?: string; commitId?: string };
  scenarioIds?: string[];
  channels?: ScenarioChannel[];
}

export interface ScenarioGateRequired {
  scenario_id: string;
  scenario_name: string;
  transport: ScenarioTransport;
  modality: ScenarioModality;
  result: ReplayResult | 'not_run';
}

export interface ScenarioGate {
  enabled: boolean;
  commit_id: string | null;
  status: 'pass' | 'fail' | 'not_run' | 'disabled';
  required: ScenarioGateRequired[];
  last_run_id: string | null;
}

export interface ScenarioListParams {
  q?: string;
  tag?: string;
}

export const scenariosApi = {
  list: async (params?: ScenarioListParams) =>
    (await apiClient.get<Scenario[]>('/agent-improvement/scenarios', { params })).data,

  get: async (id: string) =>
    (await apiClient.get<Scenario>(`/agent-improvement/scenarios/${id}`)).data,

  create: async (input: CreateScenarioInput) =>
    (await apiClient.post<Scenario>('/agent-improvement/scenarios', input)).data,

  update: async (id: string, input: UpdateScenarioInput) =>
    (await apiClient.put<Scenario>(`/agent-improvement/scenarios/${id}`, input)).data,

  delete: async (id: string) => {
    await apiClient.delete(`/agent-improvement/scenarios/${id}`);
  },

  duplicate: async (id: string) =>
    (await apiClient.post<Scenario>(`/agent-improvement/scenarios/${id}/duplicate`)).data,

  draft: async (description: string) =>
    (await apiClient.post<ScenarioDraft>('/agent-improvement/scenarios/draft', { description }))
      .data,
};

export const scenarioAssignmentsApi = {
  list: async (params?: { configuration_id?: string; scenario_id?: string }) =>
    (
      await apiClient.get<ScenarioAssignment[]>('/agent-improvement/scenario-assignments', {
        params,
      })
    ).data,

  create: async (input: CreateAssignmentInput) =>
    (await apiClient.post<ScenarioAssignment>('/agent-improvement/scenario-assignments', input))
      .data,

  update: async (id: string, input: UpdateAssignmentInput) =>
    (
      await apiClient.patch<ScenarioAssignment>(
        `/agent-improvement/scenario-assignments/${id}`,
        input,
      )
    ).data,

  delete: async (id: string) => {
    await apiClient.delete(`/agent-improvement/scenario-assignments/${id}`);
  },
};

export const scenarioRunsApi = {
  list: async (params?: { configuration_id?: string; scenario_id?: string }) =>
    (await apiClient.get<ScenarioRun[]>('/agent-improvement/scenario-runs', { params })).data,

  get: async (id: string) =>
    (await apiClient.get<ScenarioRun>(`/agent-improvement/scenario-runs/${id}`)).data,

  create: async (input: CreateScenarioRunInput) =>
    (await apiClient.post<ScenarioRun>('/agent-improvement/scenario-runs', input)).data,

  cancel: async (id: string) =>
    (await apiClient.post<ScenarioRun>(`/agent-improvement/scenario-runs/${id}/cancel`)).data,

  rerunFailed: async (id: string) =>
    (await apiClient.post<ScenarioRun>(`/agent-improvement/scenario-runs/${id}/rerun-failed`)).data,

  adjust: async (id: string) =>
    (await apiClient.post<{ id: string }>(`/agent-improvement/scenario-runs/${id}/adjust`)).data,
};

export const scenarioGateApi = {
  get: async (configurationId: string, commitId?: string | null) =>
    (
      await apiClient.get<ScenarioGate>(`/agent-improvement/scenario-gate/${configurationId}`, {
        params: commitId ? { commit_id: commitId } : undefined,
      })
    ).data,
};
